export const SortSameDocument = Object.freeze({
    PAGE_WISE: 'pageWise',
    RANK_WISE: 'rankWise',
});

/**
 * Sort chunks by document relevance, then within each document
 *
 * @param {Array<Object>} chunks
 * @param {SortSameDocument[keyof SortSameDocument]} sortSameDocument
 * @returns {Array<Object>}
 */
export function sortChunks(
    chunks,
    sortSameDocument = SortSameDocument.PAGE_WISE
) {
    if (!Array.isArray(chunks)) return [];

    const documentMap = new Map();

    // Attach original index ONLY internally for stability
    chunks.forEach((chunk, index) => {
        const document = chunk.document;
        if (!documentMap.has(document)) {
            documentMap.set(document, []);
        }
        documentMap.get(document).push({ ...chunk, __index: index });
    });

    const documentBuckets = Array.from(documentMap.values()).map(
        (chunksOfDoc) => {
            const ranks = chunksOfDoc
                .map((c) => c.rank)
                .filter((r) => Number.isFinite(r));

            const minRank = ranks.length
                ? Math.min(...ranks)
                : Number.MAX_SAFE_INTEGER;

            chunksOfDoc.sort((a, b) => {
                // Explicit RANK_WISE
                if (sortSameDocument === SortSameDocument.RANK_WISE) {
                    if (Number.isFinite(a.rank) && Number.isFinite(b.rank)) {
                        return a.rank - b.rank;
                    }
                    if (Number.isFinite(a.rank)) return -1;
                    if (Number.isFinite(b.rank)) return 1;
                    return a.__index - b.__index;
                }

                // PAGE_WISE with full fallback chain
                const hasPageA = Number.isFinite(a.pageNumber);
                const hasPageB = Number.isFinite(b.pageNumber);
                const hasRankA = Number.isFinite(a.rank);
                const hasRankB = Number.isFinite(b.rank);

                if (hasPageA && hasPageB) {
                    return a.pageNumber - b.pageNumber;
                }

                if (hasRankA && hasRankB) {
                    return a.rank - b.rank;
                }

                if (hasPageA || hasRankA) return -1;
                if (hasPageB || hasRankB) return 1;

                // No page, no rank → preserve original order
                return a.__index - b.__index;
            });

            return { minRank, chunks: chunksOfDoc };
        }
    );

    // Sort documents by best available rank
    documentBuckets.sort((a, b) => a.minRank - b.minRank);

    // Strip internal metadata
    return documentBuckets.flatMap((bucket) =>
        bucket.chunks.map(({ __index, ...chunk }) => chunk)
    );
}
